import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { useTranslation } from "react-i18next";

const LoadProfileChart = ({ data, selectedTrip, color }) => {

  const [t] = useTranslation('common');

  const chartRef = useRef();
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {

    // Time points in seconds array for conversion to date
    let realPointsY = data.map((e) => {
      return e.occupancy_status
    })

    // Unique shape dist traveled for x Scale domain
    let unique_sdt = [...new Set(data.map(i => i.shape_dist_traveled))];

    // Tick labels (text)
    let tickLabels = data.map((e) => { return e.stop_name });
    let tickLabelsFormat = [tickLabels[0], tickLabels[tickLabels.length - 1]];

    const margins = { top: 100, right: 75, bottom: 50, left: 75 };
    const width = chartRef.current.parentElement.offsetWidth;
    const height = chartRef.current.parentElement.offsetHeight;
    const innerWidth = width - margins.left - margins.right;
    const innerHeight = height - margins.top - margins.bottom;

    //X and Y scale
    let xScale = d3.scaleLinear()
      .domain([d3.min(unique_sdt), d3.max(unique_sdt)])
      .range([0, innerWidth])

    let yScale = d3.scaleLinear()
      .domain(d3.extent(realPointsY))
      .range([innerHeight, 0])


    //Setting chart width and adjusting for margins
    const chart = d3.select(chartRef.current)
      .attr('width', innerWidth + margins.right + margins.left)
      .attr('height', height + margins.top + margins.bottom)
      .append('g')
      .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')')
      .attr('id', 'chartid')


    // Chart background and outline
    chart.append("rect")
      .attr("width", width)
      .attr("height", height)
      .attr('transform', 'translate(' + -margins.left + ',' + -margins.top + ')')
      .attr("fill", "white")
      .attr("stroke", "#dee2e6")
      .attr("rx", 6)
      .attr("ry", 6)

    //Create tooltip
    const tooltip = d3.select('.container').append('div')
      .attr('class', 'tooltipotp')
      .html('Tooltip')
      .attr('id', 'tooltipid')


    // Format lines for planned series
    const line = d3.line()
      .x(function (d) {
        return xScale(d.shape_dist_traveled);
      })
      .y(function (d) {
        return yScale(d.occupancy_status)
      })

    // Add lines for planned series
    chart.append("path")
      .datum(data)
      .attr("class", "line")
      .attr("d", d => line(d))
      .attr("stroke", color)
      .attr('transform', 'translate(10,' + 0 + ')')
      .style("stroke-width", 1)
      .style("fill", "none")


    // Add dots
    chart.selectAll('g')
      .data(data).enter().append('g')
      .append('circle')
      .attr("cx", function (d) {
        return xScale(d.shape_dist_traveled);
      })
      .attr("cy", function (d) {
        return yScale(d.occupancy_status)
      })
      .attr("r", 3)
      .attr("stroke", color)
      .attr("fill", "white")
      .attr('transform', 'translate(10,' + 0 + ')')
      .on('mouseover', d => {
        d3.select(d.target).attr("r", 10)
        chart.mouse = d3.pointer(d);
        chart.mouse2 = d3.pointer(d, d3.select(chart.chartLocation));
        tooltip.html(
          t('tripocc.graph.stop') + d.srcElement.__data__.stop_name + '<br/>' +
          t('tripocc.graph.occhover') + d.srcElement.__data__.occupancy_status.toFixed(1))
          .style('left', chart.mouse2[0] - 75 + 'px')
          .style('top', chart.mouse2[1] - 150 + 'px')
          .style('opacity', .9);
      }).on('mouseout', (d) => {
        d3.select(d.target).attr("r", 3)
        tooltip.style('opacity', 0)
          .style('left', '0px');
      }).on("mouseleave", () => {
        tooltip.style('opacity', 0)
          .style('left', '0px');
      })

    //Append x axis
    chart.append('g')
      .attr('transform', 'translate(10,' + innerHeight + ')')
      .call(d3.axisBottom(xScale)
        .tickValues([xScale.domain()[0], xScale.domain()[1]])
        .tickFormat((d, i) => {
          if (width > 999) {
            return tickLabelsFormat[i]
          }
        })
      )
      .selectAll("text")
      .style("text-anchor", (d, i) => i === 0 ? "start" : "end") // Align the first tick to the start (left), and the last tick to the end (right)
      .attr("font-family", "Roboto")
      .style("font-size", "15px")
      .attr('class', 'x-axis')


    //Append y axis
    chart.append('g')
      .call(d3.axisLeft(yScale)
        // .tickFormat(timeFormat)
        .ticks(4)
      )
      .attr('class', 'yAxis')
      .attr("font-family", "Roboto")
      .style("font-size", "15px")
      .attr('class', 'y-axis')

    var axisLabelX = -50;
    var axisLabelY = innerHeight / 2;

    //Append y axis label
    chart.append('g')
      .attr('transform', 'translate(' + axisLabelX + ', ' + axisLabelY + ')')
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('transform', 'rotate(-90)')
      .text(t('tripocc.graph.occ'))



    //Append chart title
    chart.append("text")
      .attr("x", (innerWidth / 2))
      .attr("y", 0 - (margins.top / 2))
      .attr("text-anchor", "middle")
      .attr("font-family", "Roboto")
      .style("font-size", "24px")
      .text(selectedTrip);


    function updateGraph() {

      //Redraw graph is window is resized
      d3.select("g").remove()
      setRefreshKey(oldKey => oldKey + 1);
    }

    d3.select(window).on('resize.updatesvg', updateGraph);

  }, [data, refreshKey]);


  return <svg ref={chartRef}></svg>;
};

export default LoadProfileChart;